import {
  ability,
  ACTIONS,
  PERMISSION_SUBJECTS,
  type PermissionSubjects,
} from '@/helpers/casl/ability';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import {
  selectedReviewMemberAtom,
  useCombinedQueriesTeamGeneralInfo,
} from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

export const useRBACPermissionCheck = (isNotCreatedBySessionOwner?: boolean) => {
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const canEditOtherUserDoc =
    (isReviewingOtherUser &&
      ability.can(ACTIONS.ACCESS, PERMISSION_SUBJECTS.TEAM_OTHERS_DOC_EDIT) &&
      isNotCreatedBySessionOwner) ??
    false;

  const checkRBACPermission = useCallback(
    (
      reviewingOtherUserPermission: PermissionSubjects,
      notReviewingOtherUserPermission?: PermissionSubjects
    ) => {
      if (isReviewingOtherUser) {
        return ability.can(ACTIONS.ACCESS, reviewingOtherUserPermission);
      } else if (notReviewingOtherUserPermission) {
        return ability.can(ACTIONS.ACCESS, notReviewingOtherUserPermission);
      }
      return !isReviewingOtherUser;
    },
    [isReviewingOtherUser]
  );

  const isReviewPrimaryNoteOnly = useMemo(() => {
    if (canEditOtherUserDoc) {
      return false;
    }

    if (isReviewingOtherUser) {
      const teamMember = teamDetails?.team.team_members?.find(
        (member) => member.kinde_user_id === selectedReviewUser
      );
      if (teamMember) {
        return true;
      }
    }
    return false;
  }, [isReviewingOtherUser, selectedReviewUser, teamDetails, canEditOtherUserDoc]);

  const isDocumentEditable = useMemo(() => {
    if (isReviewingOtherUser) {
      return canEditOtherUserDoc;
    }
    return true;
  }, [canEditOtherUserDoc, isReviewingOtherUser]);

  return {
    checkRBACPermission,
    isReviewingOtherUser,
    isReviewPrimaryNoteOnly,
    isDocumentEditable,
  };
};
